import { useState, useEffect, useRef } from 'react';
import { invoices, clients, notifications } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Receipt, DollarSign, Trash2, MoreVertical, CreditCard, Bell, Mail, Send, AlertTriangle, Printer, Eye, Upload, Download, FileSpreadsheet, Search, X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import InvoicePrint from '@/components/InvoicePrint';
import { useAuth } from '@/contexts/AuthContext';

const NCF_TYPE_LABELS = {
  '01': 'Crédito Fiscal',
  '02': 'Consumo',
  '14': 'Zona Franca',
  '15': 'Gubernamental',
};

const statusLabels = {
  draft: 'Borrador',
  sent: 'Enviada',
  partial: 'Pago Parcial',
  paid: 'Pagada',
  overdue: 'Vencida',
};

const statusColors = {
  draft: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  sent: 'badge-warning',
  partial: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  paid: 'badge-success',
  overdue: 'badge-danger',
};

function CreateInvoiceDialog({ open, onOpenChange, onSubmit, clientsList }) {
  const [formData, setFormData] = useState({
    client_id: '',
    ncf_type_override: '',
    notes: '',
    due_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
  });

  const selectedClient = clientsList.find(c => c.client_id === formData.client_id);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }],
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const tax = subtotal * 0.18; // ITBIS 18% República Dominicana
  const total = subtotal + tax;

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      client_id: formData.client_id,
      items: formData.items.filter(item => item.description),
      notes: formData.notes,
    };
    if (formData.ncf_type_override) {
      data.ncf_type_override = formData.ncf_type_override;
    }
    if (formData.due_date) {
      data.due_date = new Date(formData.due_date).toISOString();
    }
    onSubmit(data);
    setFormData({
      client_id: '',
      ncf_type_override: '',
      notes: '',
      due_date: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Nueva Factura</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger data-testid="invoice-client-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientsList.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name} {client.rnc ? `(RNC: ${client.rnc})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Fecha de vencimiento</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
          </div>

          {/* NCF Type Selection */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
            <Label className="text-blue-800 font-semibold">Tipo de Comprobante Fiscal (NCF)</Label>
            <Select
              value={formData.ncf_type_override || (selectedClient?.client_type === 'credito_fiscal' ? '01' : '02')}
              onValueChange={(value) => setFormData({ ...formData, ncf_type_override: value })}
            >
              <SelectTrigger data-testid="invoice-ncf-type-select" className="bg-white">
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="01">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">01</span>
                    <span>Factura de Crédito Fiscal</span>
                    <Badge variant="secondary" className="text-xs">Requiere RNC</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="02">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">02</span>
                    <span>Factura de Consumo</span>
                  </div>
                </SelectItem>
                <SelectItem value="14">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">14</span>
                    <span>Zona Franca / Régimen Especial</span>
                    <Badge variant="secondary" className="text-xs">Requiere RNC</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="15">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">15</span>
                    <span>Comprobante Gubernamental</span>
                    <Badge variant="secondary" className="text-xs">Requiere RNC</Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-blue-600">
              {formData.ncf_type_override === '01' || (!formData.ncf_type_override && selectedClient?.client_type === 'credito_fiscal')
                ? 'Para ventas a empresas registradas en DGII. Sustenta gastos ISR y créditos ITBIS.'
                : formData.ncf_type_override === '14'
                ? 'Para ventas a empresas en zonas francas o regímenes tributarios especiales.'
                : formData.ncf_type_override === '15'
                ? 'Para ventas a entidades gubernamentales.'
                : 'Para ventas a consumidores finales. No sustenta crédito fiscal.'}
            </p>
          </div>

          {/* Items */}
          <div className="space-y-2">
            <Label>Conceptos</Label>
            <div className="space-y-2">
              {formData.items.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <Input
                    placeholder="Descripción"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    className="flex-1"
                    data-testid={`invoice-item-desc-${index}`}
                  />
                  <Input
                    type="number"
                    placeholder="Cant."
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                    className="w-20"
                    min="1"
                  />
                  <Input
                    type="number"
                    placeholder="Precio"
                    value={item.unit_price}
                    onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                    className="w-28"
                    min="0"
                    step="0.01"
                  />
                  <div className="w-24 text-right font-mono pt-2">
                    {formatCurrency(item.quantity * item.unit_price)}
                  </div>
                  {formData.items.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus className="h-4 w-4 mr-1" />
              Agregar concepto
            </Button>
          </div>

          {/* Totals */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>ITBIS (18%)</span>
              <span className="font-mono">{formatCurrency(tax)}</span>
            </div>
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span className="font-mono">{formatCurrency(total)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="invoice-submit-btn">
              Crear Factura
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, onOpenChange, onSubmit, invoice }) {
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (invoice) {
      setAmount(String(invoice.total - invoice.paid_amount));
    }
  }, [invoice]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(parseFloat(amount));
    setAmount('');
  };

  if (!invoice) return null;

  const pending = invoice.total - invoice.paid_amount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Registrar Pago</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Factura: <span className="font-mono font-medium">{invoice.invoice_number}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Total: <span className="font-mono font-medium">{formatCurrency(invoice.total)}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Pagado: <span className="font-mono font-medium text-emerald-500">{formatCurrency(invoice.paid_amount)}</span>
            </p>
            <p className="text-sm">
              Pendiente: <span className="font-mono font-semibold text-amber-500">{formatCurrency(pending)}</span>
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="payment-amount">Monto a pagar</Label>
            <Input
              id="payment-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={pending}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              data-testid="payment-amount-input"
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="payment-submit-btn">
              <CreditCard className="h-4 w-4 mr-2" />
              Registrar Pago
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const [invoicesList, setInvoicesList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState({ overdue: [], pending: [], total_overdue: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, clientsRes, pendingRes] = await Promise.all([
        invoices.getAll(),
        clients.getAll(),
        notifications.getPendingInvoices(7),
      ]);
      setInvoicesList(invoicesRes.data);
      setClientsList(clientsRes.data);
      setPendingNotifications(pendingRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await invoices.create(data);
      toast.success('Factura creada exitosamente');
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear factura');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await invoices.downloadTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_facturas_transtar.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla descargada');
    } catch (err) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await invoices.exportExcel(filter !== 'all' ? filter : undefined);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_transtar_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Facturas exportadas');
    } catch (err) {
      toast.error('Error al exportar facturas');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await invoices.importExcel(file);
      if (res.data.success) {
        toast.success(`${res.data.imported_count} facturas importadas exitosamente`);
        if (res.data.errors?.length > 0) {
          toast.warning(`${res.data.errors.length} errores encontrados`);
        }
        setImportDialogOpen(false);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar facturas');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpdateStatus = async (invoiceId, status) => {
    try {
      await invoices.update(invoiceId, { status });
      toast.success('Estado actualizado');
      loadData();
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleSendReminder = async (invoiceId) => {
    setSendingReminder(true);
    try {
      const res = await notifications.sendReminder(invoiceId);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }
    } catch (err) {
      toast.error('Error al enviar recordatorio');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSendBulkReminders = async () => {
    setSendingReminder(true);
    try {
      const res = await notifications.sendBulkReminders(7);
      toast.success(`Enviados: ${res.data.sent}, Fallidos: ${res.data.failed}, Omitidos: ${res.data.skipped}`);
      loadData();
    } catch (err) {
      toast.error('Error al enviar recordatorios');
    } finally {
      setSendingReminder(false);
    }
  };

  const handlePayment = async (amount) => {
    if (!selectedInvoice) return;
    try {
      const newPaidAmount = selectedInvoice.paid_amount + amount;
      await invoices.update(selectedInvoice.invoice_id, { paid_amount: newPaidAmount });
      toast.success('Pago registrado exitosamente');
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      loadData();
    } catch (err) {
      toast.error('Error al registrar pago');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const openPrintDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPrintDialogOpen(true);
  };

  // Stats
  const totalInvoiced = invoicesList.reduce((sum, inv) => sum + inv.total, 0);
  const totalPaid = invoicesList.reduce((sum, inv) => sum + inv.paid_amount, 0);
  const totalPending = totalInvoiced - totalPaid;
  const paidCount = invoicesList.filter(inv => inv.status === 'paid').length;
  const pendingCount = invoicesList.filter(inv => ['sent', 'partial'].includes(inv.status)).length;

  // Filter and Search
  const filteredInvoices = invoicesList.filter(inv => {
    // Status filter
    if (filter !== 'all') {
      if (filter === 'pending' && !['sent', 'partial', 'draft'].includes(inv.status)) return false;
      else if (filter !== 'pending' && inv.status !== filter) return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchInvoiceNumber = inv.invoice_number?.toLowerCase().includes(query);
      const matchClientName = inv.client_name?.toLowerCase().includes(query);
      const matchNCF = inv.ncf?.toLowerCase().includes(query);
      const matchDate = formatDate(inv.created_at)?.toLowerCase().includes(query);
      const matchDueDate = inv.due_date ? formatDate(inv.due_date)?.toLowerCase().includes(query) : false;
      
      if (!matchInvoiceNumber && !matchClientName && !matchNCF && !matchDate && !matchDueDate) {
        return false;
      }
    }
    
    return true;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'client_name':
        valA = (a.client_name || '').toLowerCase();
        valB = (b.client_name || '').toLowerCase();
        break;
      case 'total':
        valA = a.total || 0;
        valB = b.total || 0;
        break;
      case 'balance':
        valA = (a.total || 0) - (a.paid_amount || 0);
        valB = (b.total || 0) - (b.paid_amount || 0);
        break;
      case 'due_date':
        valA = a.due_date || '';
        valB = b.due_date || '';
        break;
      default:
        valA = a.created_at || '';
        valB = b.created_at || '';
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Facturación</h1>
          <p className="text-muted-foreground">Gestión de facturas y pagos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-invoices-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-invoices-btn">
              <Upload className="h-4 w-4 mr-2" />
              Importar Excel
            </Button>
          )}
          <Button variant="outline" onClick={() => setDialogOpen(true)} data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Factura
          </Button>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Importar Facturas desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-3">
                Descarga la plantilla Excel, llénala con tus facturas antiguas, y súbela aquí.
              </p>
              <Button variant="outline" onClick={handleDownloadTemplate} className="w-full gap-2">
                <Download className="h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Archivo Excel</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                disabled={importing}
                data-testid="invoice-excel-input"
              />
              <p className="text-xs text-muted-foreground">
                Solo archivos .xlsx o .xls
              </p>
            </div>

            {importing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                Procesando archivo...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification Alert */}
      {(pendingNotifications.total_overdue > 0 || pendingNotifications.total_pending > 0) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-full">
                  <Bell className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-semibold">Facturas por vencer</p>
                  <p className="text-sm text-muted-foreground">
                    {pendingNotifications.total_overdue > 0 && (
                      <span className="text-rose-500 font-medium">{pendingNotifications.total_overdue} vencidas</span>
                    )}
                    {pendingNotifications.total_overdue > 0 && pendingNotifications.total_pending > 0 && ' • '}
                    {pendingNotifications.total_pending > 0 && (
                      <span className="text-amber-500 font-medium">{pendingNotifications.total_pending} próximas a vencer</span>
                    )}
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleSendBulkReminders} 
                disabled={sendingReminder}
                className="gap-2"
                data-testid="send-bulk-reminders-btn"
              >
                <Send className="h-4 w-4" />
                {sendingReminder ? 'Enviando...' : 'Enviar Recordatorios'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-success">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Total Facturado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{formatCurrency(totalInvoiced)}</p>
          </CardContent>
        </Card>
        <Card className="kpi-success">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Cobrado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading text-emerald-500">{formatCurrency(totalPaid)}</p>
            <p className="text-xs text-muted-foreground">{paidCount} facturas pagadas</p>
          </CardContent>
        </Card>
        <Card className="kpi-warning">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Por Cobrar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading text-amber-500">{formatCurrency(totalPending)}</p>
            <p className="text-xs text-muted-foreground">{pendingCount} facturas pendientes</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Tasa de Cobro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">
              {totalInvoiced > 0 ? Math.round((totalPaid / totalInvoiced) * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar por número, cliente, NCF o fecha..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="invoice-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'Todas' },
            { value: 'pending', label: 'Pendientes' },
            { value: 'paid', label: 'Pagadas' },
          ].map((option) => (
            <Button
              key={option.value}
              variant={filter === option.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Results counter */}
        {(searchQuery || filter !== 'all') && (
          <span className="text-sm text-muted-foreground">
            {filteredInvoices.length} de {invoicesList.length} facturas
          </span>
        )}
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {filteredInvoices.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No hay facturas registradas
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs cursor-pointer select-none" onClick={() => toggleSort('created_at')} data-testid="sort-invoice-date">
                    Factura {sortField === 'created_at' && (sortDir === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="uppercase text-xs">NCF</TableHead>
                  <TableHead className="uppercase text-xs cursor-pointer select-none" onClick={() => toggleSort('client_name')} data-testid="sort-invoice-client">
                    Cliente {sortField === 'client_name' && (sortDir === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="uppercase text-xs cursor-pointer select-none" onClick={() => toggleSort('due_date')} data-testid="sort-invoice-due">
                    Fecha {sortField === 'due_date' && (sortDir === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="uppercase text-xs text-right cursor-pointer select-none" onClick={() => toggleSort('total')} data-testid="sort-invoice-total">
                    Total {sortField === 'total' && (sortDir === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="uppercase text-xs text-right cursor-pointer select-none" onClick={() => toggleSort('balance')} data-testid="sort-invoice-balance">
                    Saldo {sortField === 'balance' && (sortDir === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="uppercase text-xs">Estado</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedInvoices.map((invoice) => {
                  const balance = invoice.total - invoice.paid_amount;
                  const isOverdue = invoice.due_date && 
                    new Date(invoice.due_date) < new Date() && 
                    invoice.status !== 'paid';
                  
                  return (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell>
                        <div className="font-mono font-medium">{invoice.invoice_number}</div>
                        <div className="text-xs text-muted-foreground">{formatDate(invoice.created_at)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm font-semibold text-primary">{invoice.ncf || 'N/A'}</div>
                        <Badge variant="outline" className="text-xs mt-1">
                          {NCF_TYPE_LABELS[invoice.ncf_type] || 'Consumo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{invoice.client_name}</div>
                        {invoice.client_rnc && (
                          <div className="text-xs text-muted-foreground">RNC: {invoice.client_rnc}</div>
                        )}
                      </TableCell>
                      <TableCell className={`font-mono text-sm ${isOverdue ? 'text-rose-500' : ''}`}>
                        {invoice.due_date ? formatDate(invoice.due_date) : '-'}
                        {isOverdue && <div className="text-xs">Vencida</div>}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${balance > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                        {formatCurrency(balance)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={isOverdue ? statusColors.overdue : statusColors[invoice.status]}>
                          {isOverdue ? 'Vencida' : statusLabels[invoice.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => openPrintDialog(invoice)}
                            data-testid={`print-invoice-${invoice.invoice_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openPrintDialog(invoice)}>
                                <Printer className="h-4 w-4 mr-2" />
                                Ver / Imprimir
                              </DropdownMenuItem>
                              {invoice.status === 'draft' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.invoice_id, 'sent')}>
                                  Marcar como enviada
                                </DropdownMenuItem>
                              )}
                              {invoice.status !== 'paid' && (
                                <>
                                  <DropdownMenuItem onClick={() => openPaymentDialog(invoice)}>
                                    <DollarSign className="h-4 w-4 mr-2" />
                                    Registrar pago
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSendReminder(invoice.invoice_id)}>
                                    <Mail className="h-4 w-4 mr-2" />
                                    Enviar recordatorio
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateInvoiceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleCreate}
        clientsList={clientsList}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        onSubmit={handlePayment}
        invoice={selectedInvoice}
      />

      <InvoicePrint
        invoiceId={selectedInvoice?.invoice_id}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </div>
  );
}
